import { useSyncExternalStore, type AnchorHTMLAttributes, type MouseEvent } from 'react';

/** A small history-API router. The app's routing needs are simple and this keeps it dependency-free. */
const listeners = new Set<() => void>();
const emit = () => listeners.forEach((listener) => listener());

if (typeof window !== 'undefined') window.addEventListener('popstate', emit);

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

export function navigate(to: string, options: { replace?: boolean } = {}) {
  if (`${window.location.pathname}${window.location.search}` === to) return;
  window.history[options.replace ? 'replaceState' : 'pushState'](null, '', to);
  emit();
  window.scrollTo({ top: 0 });
}

export function usePathname(): string {
  return useSyncExternalStore(subscribe, () => window.location.pathname, () => '/');
}

/** Matches "/clients/:id" style patterns. Returns params, or null when the path does not match. */
export function matchPath(pattern: string, pathname: string): Record<string, string> | null {
  const patternParts = pattern.split('/').filter(Boolean);
  const pathParts = pathname.split('/').filter(Boolean);
  if (patternParts.length !== pathParts.length) return null;
  const params: Record<string, string> = {};
  for (const [index, part] of patternParts.entries()) {
    if (part.startsWith(':')) params[part.slice(1)] = decodeURIComponent(pathParts[index]);
    else if (part !== pathParts[index]) return null;
  }
  return params;
}

type LinkProps = AnchorHTMLAttributes<HTMLAnchorElement> & { to: string };
export function Link({ to, onClick, ...props }: LinkProps) {
  function handleClick(event: MouseEvent<HTMLAnchorElement>) {
    onClick?.(event);
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    navigate(to);
  }
  return <a href={to} onClick={handleClick} {...props} />;
}

/** The prototype used hash routes (#clients, #client-profile:id). Bookmarks to them keep working. */
export function redirectLegacyHash() {
  const hash = window.location.hash.slice(1);
  if (!hash) return;
  const aliases: Record<string, string> = { dashboard: '/', map: '/service-map', 'service-map': '/service-map', rodent: '/rodent-planning', 'rodent-planning': '/rodent-planning', calculator: '/chemical-calculator', 'chemical-calculator': '/chemical-calculator', documents: '/records', 'technician-reports': '/reports/technicians' };
  let target = aliases[hash] ?? null;
  if (!target && hash.startsWith('client-profile:')) target = `/clients/${hash.slice('client-profile:'.length)}`;
  if (!target && /^[a-z-]+$/.test(hash)) target = `/${hash}`;
  if (target) window.history.replaceState(null, '', target);
}
